import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMe, useUsers, useRoles, useNotificationPreferences, useUpdateNotificationPreferences } from '@/hooks/queries';
import { api } from '@/lib/api';

export function SettingsPage() {
  const { t } = useTranslation();
  const { data: me, isLoading: meLoading } = useMe();

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold tracking-tight font-display">{t('settings.title')}</h1>

      <Tabs defaultValue="office">
        <TabsList>
          <TabsTrigger value="office">{t('settings.office')}</TabsTrigger>
          <TabsTrigger value="staff">{t('settings.staff')}</TabsTrigger>
          <TabsTrigger value="roles">{t('settings.roles')}</TabsTrigger>
          <TabsTrigger value="notifications">Ειδοποιήσεις</TabsTrigger>
        </TabsList>

        {/* Office tab */}
        <TabsContent value="office">
          <Card className="rounded-2xl border-border shadow-lifted">
            <CardHeader>
              <CardTitle className="font-display">{t('settings.office')}</CardTitle>
              <CardDescription>Στοιχεία γραφείου και συνδρομή</CardDescription>
            </CardHeader>
            <CardContent>
              {meLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : me ? (
                <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                  <div>
                    <p className="text-xs font-bold text-muted-foreground">Γραφείο</p>
                    <p className="font-medium">{me.officeName}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-muted-foreground">Συνδρομή</p>
                    <Badge className="rounded-full">{me.officeTier}</Badge>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-muted-foreground">Email</p>
                    <p className="font-medium">{me.email}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-muted-foreground">Ρόλος</p>
                    <p className="font-medium">{me.roleTemplateName ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-muted-foreground">Δικαιώματα</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {me.capabilities.map((cap) => (
                        <Badge key={cap} variant="outline" className="text-xs rounded-full status-new">{cap}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Δεν ήταν δυνατή η φόρτωση</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff tab */}
        <TabsContent value="staff">
          <StaffTab />
        </TabsContent>

        {/* Roles tab */}
        <TabsContent value="roles">
          <RolesTab />
        </TabsContent>

        {/* Notifications tab */}
        <TabsContent value="notifications">
          <NotificationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StaffTab() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useUsers();
  const { data: rolesData } = useRoles();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', roleTemplateId: '' });
  const [inviting, setInviting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const roles = rolesData?.data ?? [];

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post('/users', {
        email: inviteForm.email,
        name: inviteForm.name,
        roleTemplateId: inviteForm.roleTemplateId || undefined,
      });
      setInviteForm({ name: '', email: '', roleTemplateId: '' });
      setInviteOpen(false);
      refetch();
    } finally {
      setInviting(false);
    }
  };

  const handleToggleActive = async (userId: string, currentlyActive: boolean) => {
    setTogglingId(userId);
    try {
      await api.patch(`/users/${userId}`, { isActive: !currentlyActive });
      refetch();
    } finally {
      setTogglingId(null);
    }
  };

  const handleRoleChange = async (userId: string, roleTemplateId: string) => {
    await api.patch(`/users/${userId}`, { roleTemplateId: roleTemplateId || null });
    refetch();
  };

  const handleResendInvite = async (userId: string) => {
    setResendingId(userId);
    try {
      await api.post(`/users/${userId}/resend-invite`, {});
    } finally {
      setResendingId(null);
    }
  };

  return (
    <Card className="rounded-2xl border-border shadow-lifted">
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle className="font-display">{t('settings.staff')}</CardTitle>
          <CardDescription>Μέλη ομάδας και ρόλοι</CardDescription>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Πρόσκληση Χρήστη
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Νέος Χρήστης</DialogTitle>
              <DialogDescription>Προσθήκη μέλους στην ομάδα</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <Label className="text-xs font-bold text-muted-foreground">Όνομα *</Label>
                <Input value={inviteForm.name} onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))} required className="rounded-[10px] bg-surface-2" />
              </div>
              <div>
                <Label className="text-xs font-bold text-muted-foreground">Email *</Label>
                <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} required className="rounded-[10px] bg-surface-2" />
              </div>
              <div>
                <Label className="text-xs font-bold text-muted-foreground">Ρόλος</Label>
                <Select value={inviteForm.roleTemplateId} onValueChange={(v) => setInviteForm((f) => ({ ...f, roleTemplateId: v === '__none__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Επιλογή ρόλου" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Κανένας</SelectItem>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Ακύρωση</Button>
                <Button type="submit" disabled={inviting}>{inviting ? 'Αποθήκευση...' : 'Πρόσκληση'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <Table className="text-[13px]">
        <TableHeader>
          <TableRow>
            <TableHead className="text-[11px] uppercase tracking-wider">Όνομα</TableHead>
            <TableHead className="text-[11px] uppercase tracking-wider">Email</TableHead>
            <TableHead className="text-[11px] uppercase tracking-wider">Ρόλος</TableHead>
            <TableHead className="text-[11px] uppercase tracking-wider">Πελάτες</TableHead>
            <TableHead className="text-[11px] uppercase tracking-wider">Κατάσταση</TableHead>
            <TableHead className="text-[11px] uppercase tracking-wider">Ενέργειες</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 6 }).map((_, j) => (
                  <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                ))}
              </TableRow>
            ))
          ) : !data?.data.length ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                Δεν υπάρχουν χρήστες
              </TableCell>
            </TableRow>
          ) : (
            data.data.map((user) => (
              <TableRow key={user.id} className="table-row-hover">
                <TableCell className="font-medium">{user.name}</TableCell>
                <TableCell className="text-muted-foreground">{user.email}</TableCell>
                <TableCell>
                  <Select
                    value={user.roleTemplateId ?? '__none__'}
                    onValueChange={(v) => handleRoleChange(user.id, v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger className="w-[140px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {roles.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>{user.assignedClientCount}</TableCell>
                <TableCell>
                  <Badge variant={user.isActive ? 'success' : 'secondary'} className="rounded-full">
                    {user.isActive ? 'Ενεργός' : 'Ανενεργός'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {user.isPending && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={resendingId === user.id}
                        onClick={() => handleResendInvite(user.id)}
                        title="Επαναποστολή πρόσκλησης"
                      >
                        <Send className="h-3.5 w-3.5 mr-1" />
                        {resendingId === user.id ? 'Αποστολή...' : 'Πρόσκληση'}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={togglingId === user.id}
                      onClick={() => handleToggleActive(user.id, user.isActive)}
                    >
                      {user.isActive ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

function RolesTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useRoles();

  return (
    <Card className="rounded-2xl border-border shadow-lifted">
      <CardHeader>
        <CardTitle className="font-display">{t('settings.roles')}</CardTitle>
        <CardDescription>Πρότυπα ρόλων και δικαιώματα</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : !data?.data.length ? (
          <p className="text-muted-foreground">Δεν υπάρχουν ρόλοι</p>
        ) : (
          <div className="space-y-4">
            {data.data.map((role) => (
              <div key={role.id} className="border border-border rounded-2xl p-4 shadow-lifted hover-lift">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-medium font-display">{role.name}</h3>
                  {role.isSystemDefault && (
                    <Badge variant="secondary" className="text-xs rounded-full">Προεπιλογή</Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {role.capabilities.map((cap) => (
                    <Badge key={cap} variant="outline" className="text-xs rounded-full status-new">{cap}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const NOTIFICATION_TYPES = [
  { key: 'deadlineReminder', label: 'Υπενθύμιση προθεσμίας', description: 'Ειδοποίηση 7, 3, 0 ημέρες πριν' },
  { key: 'deadlineOverdue', label: 'Εκπρόθεσμη υποχρέωση', description: 'Όταν μια προθεσμία λήξει' },
  { key: 'documentUploaded', label: 'Νέο έγγραφο', description: 'Όταν ένας πελάτης ανεβάσει έγγραφο' },
  { key: 'aiExtractionComplete', label: 'AI εξαγωγή', description: 'Ολοκλήρωση AI ανάλυσης εγγράφου' },
  { key: 'requestPending', label: 'Νέο αίτημα πελάτη', description: 'Όταν δημιουργηθεί αίτημα' },
] as const;

function NotificationsTab() {
  const { data, isLoading } = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();

  const prefs = data?.preferences ?? {
    deadlineReminder: { email: true, inApp: true },
    deadlineOverdue: { email: true, inApp: true },
    documentUploaded: { email: false, inApp: true },
    aiExtractionComplete: { email: false, inApp: true },
    requestPending: { email: false, inApp: true },
  };

  const toggle = (type: string, channel: 'email' | 'inApp') => {
    const current = prefs[type] ?? { email: false, inApp: true };
    const updated = {
      ...prefs,
      [type]: { ...current, [channel]: !current[channel] },
    };
    update.mutate(updated);
  };

  return (
    <Card className="rounded-2xl border-border shadow-lifted">
      <CardHeader>
        <CardTitle className="font-display">Ειδοποιήσεις</CardTitle>
        <CardDescription>Ρυθμίσεις ειδοποιήσεων ανά τύπο και κανάλι</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : (
          <Table className="text-[13px]">
            <TableHeader>
              <TableRow>
                <TableHead className="text-[11px] uppercase tracking-wider">Τύπος</TableHead>
                <TableHead className="w-24 text-center text-[11px] uppercase tracking-wider">Email</TableHead>
                <TableHead className="w-24 text-center text-[11px] uppercase tracking-wider">Εφαρμογή</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {NOTIFICATION_TYPES.map((nt) => {
                const p = prefs[nt.key] ?? { email: false, inApp: true };
                return (
                  <TableRow key={nt.key}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{nt.label}</p>
                        <p className="text-xs text-muted-foreground">{nt.description}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        type="button"
                        className={`h-5 w-10 rounded-full inline-flex items-center transition-colors ${p.email ? 'bg-primary shadow-sm' : 'bg-muted'}`}
                        onClick={() => toggle(nt.key, 'email')}
                        disabled={update.isPending}
                      >
                        <span className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${p.email ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        type="button"
                        className={`h-5 w-10 rounded-full inline-flex items-center transition-colors ${p.inApp ? 'bg-primary shadow-sm' : 'bg-muted'}`}
                        onClick={() => toggle(nt.key, 'inApp')}
                        disabled={update.isPending}
                      >
                        <span className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${p.inApp ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
